export type ArtistDirectoryItem = {
  name: string;
  categories: string[];
};

export interface ArtistDirectoryHeaderMetric {
  row: number;
  height: number;
}

export function normalizeArtistSearch(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

export function getArtistDirectoryMatches(
  items: ArtistDirectoryItem[],
  query: string,
  filter: string,
): boolean[] {
  const normalizedQuery = normalizeArtistSearch(query);

  return items.map((item) => {
    const matchesName = normalizeArtistSearch(item.name).includes(normalizedQuery);
    const matchesFilter = filter === 'all' || item.categories.includes(filter);

    return matchesName && matchesFilter;
  });
}

export function getArtistDirectoryRowHeaderHeights(
  metrics: ArtistDirectoryHeaderMetric[],
): Array<number | null> {
  const rows = new Map<number, { max: number; min: number; count: number }>();

  metrics.forEach(({ row, height }) => {
    const current = rows.get(row);

    rows.set(row, {
      max: Math.max(current?.max ?? height, height),
      min: Math.min(current?.min ?? height, height),
      count: (current?.count ?? 0) + 1,
    });
  });

  return metrics.map(({ row }) => {
    const rowMetrics = rows.get(row);

    if (!rowMetrics || rowMetrics.count < 2 || rowMetrics.max === rowMetrics.min) {
      return null;
    }

    return rowMetrics.max;
  });
}

class ArtistDirectory {
  private form: HTMLFormElement | null;
  private input: HTMLInputElement | null;
  private filter: HTMLSelectElement | null;
  private cards: HTMLElement[];
  private count: HTMLElement | null;
  private countLabel: HTMLElement | null;
  private noResults: HTMLElement | null;
  private resizeFrame: number | null = null;

  constructor(module: HTMLElement) {
    this.form = module.querySelector<HTMLFormElement>('form');
    this.input = module.querySelector<HTMLInputElement>('input[name="artist-search"]');
    this.filter = module.querySelector<HTMLSelectElement>('select[name="artist-filter"]');
    this.cards = Array.from(module.querySelectorAll<HTMLElement>('[data-artist-card]'));
    this.count = module.querySelector<HTMLElement>('[data-artist-count]');
    this.countLabel = module.querySelector<HTMLElement>('[data-artist-count-label]');
    this.noResults = module.querySelector<HTMLElement>('[data-artist-no-results]');
  }

  init() {
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.applyFilters();
    });

    window.addEventListener('resize', () => this.scheduleCardAlignment());
    document.fonts?.ready.then(() => this.alignCardRows());

    this.applyFilters();
  }

  private applyFilters() {
    const items = this.cards.map((card) => ({
      name: card.dataset.artistName || '',
      categories: (card.dataset.artistCategories || '').split(' ').filter(Boolean),
    }));
    const matches = getArtistDirectoryMatches(items, this.input?.value || '', this.filter?.value || 'all');
    let visibleCount = 0;

    this.cards.forEach((card, index) => {
      const isVisible = matches[index];
      card.hidden = !isVisible;
      if (isVisible) {
        visibleCount += 1;
      }
    });

    if (this.count) {
      this.count.textContent = `${visibleCount}`;
    }

    if (this.countLabel) {
      this.countLabel.textContent = visibleCount === 1 ? 'artist' : 'artists';
    }

    if (this.noResults) {
      this.noResults.hidden = visibleCount !== 0;
    }

    this.alignCardRows();
  }

  private scheduleCardAlignment() {
    if (this.resizeFrame !== null) {
      window.cancelAnimationFrame(this.resizeFrame);
    }

    this.resizeFrame = window.requestAnimationFrame(() => {
      this.alignCardRows();
      this.resizeFrame = null;
    });
  }

  private alignCardRows() {
    const cardHeaders = this.cards
      .filter((card) => !card.hidden)
      .map((card) => ({
        card,
        header: card.querySelector<HTMLElement>('.module__artist--header'),
      }))
      .filter((item): item is { card: HTMLElement; header: HTMLElement } => item.header !== null);

    cardHeaders.forEach(({ header }) => {
      header.style.minHeight = '';
    });

    const alignedHeights = getArtistDirectoryRowHeaderHeights(
      cardHeaders.map(({ card, header }) => ({
        row: card.offsetTop,
        height: Math.ceil(header.getBoundingClientRect().height),
      })),
    );

    cardHeaders.forEach(({ header }, index) => {
      const alignedHeight = alignedHeights[index];

      if (alignedHeight !== null) {
        header.style.minHeight = `${alignedHeight}px`;
      }
    });
  }
}

const artistDirectory = {
  init() {
    document.querySelectorAll<HTMLElement>('[data-artist-directory]').forEach((module) => {
      new ArtistDirectory(module).init();
    });
  },
};

export default artistDirectory;
